<!--
  receiving-log-edit.component.html

  The handoff-18 log-edit screen, as a modal NESTED inside the log table's Entry
  Details modal. ONE component, TWO modes — `quantity` and `items` — because the
  two halves of a log are edited with completely different controls but share
  every part that can do damage: the read, the `expected` block, the save, and
  every refusal and outcome path.

  QUANTITY mode: the container chip grid (shared with the mobile Details step), the
  print history, the rows voided on earlier edits, and the quantity before/after.
  There is no per-container list and no void ticks — the chips are the only control.
  ITEMS mode: the PO line-item check-off, the shortfall clause, and PO completion.

  Neither mode shows photos — the entry-details modal this is nested in already
  owns those.

  Four things about the editing controls are contract, not styling:
    * NOTHING here is bound to a receipt row's field. The items editor edits PO
      LINES, and planReceiptChanges() derives the sparse §3.1 patches by diffing
      the edited lines against the seeded ones — which is what makes it
      structurally impossible for this screen to echo back an untouched
      completesLine (re-opens a PO line) or isDamaged (erases the damage);
    * there is no PO field anywhere on this screen. A log's PO cannot be
      changed after submit by any endpoint (§3.5);
    * tapping a container chip DOWN is not a decrement. It tombstones specific
      item rows, because each of their ids is printed in a QR on a physical box
      (§3.3) — the save bar states how many are coming off and whether the
      survivors are renumbered, and the reason field records why;
    * the shortfall field is EDITABLE and, when a receipt quantity moved, must
      be answered before the save can go out. Nothing on this screen can
      recompute that clause, so a stale number there is a decision the user has
      to make, not one the code can make for them.

  The save OUTCOME (§4, §6, §7) is the block after the save bar: the conflict
  notice, the refusals and the save result. Two more things there are contract:
    * a non-empty `renumberedItems` is rendered as a BLOCK, never a toast — it
      means stickers already on boxes are wrong (§6.2.3). This screen does NOT
      print; it names the wrong labels and points at the log table's reprint
      action, which is where reprinting belongs;
    * the conflict block has no retry button. The PUT is not idempotent, so a
      replay doubles minted item positions and inserted receipt rows (§5.2).
-->
<ion-modal class="card-popup" [isOpen]="isOpen" (didDismiss)="close()" [backdropDismiss]="false">
  <ng-template>

    <ion-header>
      <ion-toolbar>
        <ion-title>{{ mode === 'quantity' ? 'Edit Containers' : 'Edit Items' }}</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="close()" title="Back to the entry details">
            <ion-icon name="close" slot="start"></ion-icon>
            Close
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="rle-content">

      <!-- Loading -->
      <div *ngIf="state === 'loading'" class="rle-center spinner-container">
        <ion-spinner name="crescent"></ion-spinner>
        <div class="rle-muted">Loading receiving log…</div>
      </div>

      <!-- Not found -->
      <div *ngIf="state === 'notFound'" class="rle-center">
        <ion-icon name="help-circle-outline" class="rle-state-icon"></ion-icon>
        <div class="rle-state-title">Log not found</div>
        <div class="rle-muted">
          This receiving log doesn't exist, or it was deleted or archived.
        </div>
      </div>

      <!-- Error -->
      <div *ngIf="state === 'error'" class="rle-center">
        <ion-icon name="alert-circle-outline" class="rle-state-icon rle-state-icon-error"></ion-icon>
        <div class="rle-state-title">Couldn't load this log</div>
        <div class="rle-muted">Something went wrong fetching the log details.</div>
        <button class="rle-retry-btn" (click)="loadDetails()">
          <ion-icon name="refresh-outline"></ion-icon>
          Try again
        </button>
      </div>

      <!-- Loaded -->
      <ng-container *ngIf="state === 'loaded' && details">

        <!-- THE ITEMS RIBBON — pinned to the top of the scroll container.

             The items note is assembled from controls spread down the whole page: the
             check-off, the shortfall clause and the PO-complete tick all feed the same
             recomposed string. As a card at the BOTTOM it answered "what does this
             read now?" only after you had scrolled past everything that changed it,
             which is the wrong end — by then you have forgotten which clause you were
             watching. Pinned, the sentence stays in view while you work, and each edit
             visibly moves it.

             `position: sticky`, NOT fixed or absolute: sticky occupies its own space
             in normal flow, so it pushes the page down rather than sitting on top of
             it and hiding the first card. Content does scroll UNDER it once you move
             — that is unavoidable for anything that stays visible — so it is kept to
             two short lines and given a solid background rather than a translucent one.

             `notePreview` comes from `buildLogEditRequest`, the SAME call the save
             makes, so the ribbon cannot drift from what is sent. `null` means this
             edit rewrites no note at all — the PO-complete tick is the case that lands
             there, since it writes a status row rather than the text. -->
        <!-- Only once something has MOVED. On an untouched screen the "after" line can
             only repeat the "before" line, which is a strip of screen spent saying
             nothing — and it appears at the exact moment the user has not yet given it
             anything to say. -->
        <div class="rle-ribbon" *ngIf="mode === 'items' && details.hasStructuredRows && pendingChanges">
          <div class="rle-ribbon-inner">
            <div class="rle-ribbon-line">
              <span class="rle-ribbon-label">Before</span>
              <span class="rle-ribbon-value">{{ details.log.items || '(empty)' }}</span>
            </div>
            <div class="rle-ribbon-line">
              <span class="rle-ribbon-label">After save</span>
              <!-- Three states, and the third is the one worth rendering rather than
                   hiding: an edit that changes no note text at all. A ribbon that
                   silently stopped updating would read as broken. -->
              <span class="rle-ribbon-value rle-ribbon-after"
                    *ngIf="notePreview">{{ notePreview }}</span>
              <span class="rle-ribbon-value rle-ribbon-muted"
                    *ngIf="notePreview === ''">(this will clear the items note)</span>
              <span class="rle-ribbon-value rle-ribbon-muted"
                    *ngIf="notePreview === null">unchanged by this edit</span>
            </div>
          </div>
        </div>

        <!-- Header -->
        <div class="rle-header">
          <div class="rle-header-eyebrow">
            {{ mode === 'quantity' ? 'What arrived, in containers' : 'What arrived, by item' }}
          </div>
          <!-- The PO is what a person is thinking in — it is what they looked the
               delivery up by and what the paperwork on the box says. The log id is
               an internal handle, useful for support and for nothing else, so it
               takes the smaller line. -->
          <div class="rle-header-title" *ngIf="details.log?.poNumber">PO {{ details.log.poNumber }}</div>
          <div class="rle-header-title" *ngIf="!details.log?.poNumber">No PO</div>
          <div class="rle-header-subtitle">Log #{{ details.logId }}</div>
        </div>

        <!-- Legacy notice: no structured rows to edit here -->
        <div class="rle-card rle-notice" *ngIf="!details.hasStructuredRows">
          <ion-icon name="information-circle-outline"></ion-icon>
          <span>
            This is a legacy log with no structured item or receipt rows. Its item
            list and quantity text are edited on the entry details form, not here.
          </span>
        </div>

        <!-- Soft-fail notice: a sub-fetch failed, so item/receipt rows below may be incomplete -->
        <div class="rle-card rle-notice rle-notice-warning" *ngIf="details.errorMessages">
          <ion-icon name="warning-outline"></ion-icon>
          <span>{{ details.errorMessages }}</span>
        </div>

        <!-- What the log says now. In quantity mode the container card carries its
             own before/after, so this is the items-side summary. -->
        <!-- No quantity here. Quantity is a projection of the CONTAINERS, which
             this editor cannot touch — showing it would just be a number the user
             can't act on from this screen. -->
        <!-- What the items field says NOW. The replacement is a full-width section
             at the bottom, after the edits that produce it — see there. -->
        <!-- NO "Items field text" card here, matching the container editor.

             The same redundancy the "Labels on this log" card had: the editor below
             already shows the current state, line by line, and a composed sentence
             restating it at the top is a second copy to keep in your head. The one
             thing worth showing is what the field will READ once this saves, and
             that is a single section at the bottom — after the edits that produce
             it, not above them. -->

        <!-- PO status — ITEMS MODE ONLY. Whether the PO is complete is a
             statement about what has ARRIVED, not about how it was packed. -->
        <!-- Gated on HAVING a PO, not on the PO having a status ROW.

             `details.poStatus` is null whenever `MM.ReceivingLogPoStatus` holds no
             row for the PO — i.e. whenever nobody has ever marked it complete. Gating
             the card on it meant the ticks existed only on POs that were ALREADY
             complete, so the one case the whole feature is for ("this delivery
             finished the order") had no control at all: not disabled, not explained,
             just absent. The row's absence is the default state, not a reason to hide
             the question. -->
        <div class="rle-card" *ngIf="mode === 'items' && hasPo">
          <div class="rle-receipts-title">
            PO {{ details.log.poNumber }} status
            <span class="rle-inline-hint">(applies to the whole PO, not just this log)</span>
          </div>
          <!-- TWO questions, not one, because there are two separate facts and one
               checkbox was silently conflating them.

               `MM.ReceivingLogPoStatus` is ONE ROW PER PO and records completedBy and
               completedDate — but NOT which log did it. The note's `PO COMPLETE`
               clause is the only per-log record there is. So:

                 1. did THIS delivery complete the PO?  → the note clause
                 2. is the PO complete?                 → the status row

               Either box untouched sends nothing at all for its own half, so an
               ordinary items edit never stamps completedBy on a PO nobody decided
               about, and never rewrites the note's claim.

               Ticking (1) also ticks (2) — a delivery cannot complete a PO that is
               not complete. Unticking (1) leaves (2) ALONE: another delivery may have
               finished the order, and no row says which, so reopening here would
               discard a fact nothing can reconstruct. -->
          <!-- Each tick carries its own "what does this mean" toggle. The distinction
               between them is genuinely subtle — one writes a clause on THIS log's
               note, the other writes a row for the WHOLE PO — and a label short enough
               to sit beside a checkbox cannot carry it. Collapsed by default so the
               explanation is available without turning two checkboxes into a wall of
               text on every open. -->
          <div class="rle-edit-row" *ngIf="hasPo">
            <label class="rle-void-check">
              <input type="checkbox" [checked]="logCompletesPoChecked"
                     (change)="setLogCompletesPo(!logCompletesPoChecked)" />
              <span>This delivery completed the PO</span>
            </label>
            <button type="button" class="rle-info-btn"
                    [attr.aria-expanded]="showClaimHelp"
                    aria-label="What does this mean?"
                    (click)="showClaimHelp = !showClaimHelp">
              <ion-icon name="information-circle-outline"></ion-icon>
            </button>
          </div>
          <!-- What it DOES, in two sentences. The reasoning behind the two-checkbox
               split is in the component doc where it belongs; a user opening this
               wants to know what the tick will do to their log, not why the schema
               made it necessary. -->
          <div class="rle-help-text" *ngIf="hasPo && showClaimHelp">
            Adds “PO COMPLETE” to this log’s items note, recording that this delivery
            is the one that completed the PO. It also marks the PO as fully received.
          </div>

          <!-- Asked only when this log is not claiming the completion of a PO that is
               still marked complete — the one case where the two answers can
               legitimately differ and the user may have meant both.

               A REQUIRED radio rather than a checkbox, and the change is not
               cosmetic. A checkbox has a default, and this one's default — unticked —
               silently meant "a separate delivery completed this order". That is a
               claim about the whole PO, made by nobody, on every save from a log in
               this state. Two sentences with neither preselected cannot be answered
               by not reading them: the save is held until one is chosen (see
               `reopenDecisionOutstanding`).

               Both handlers take the ANSWER, never the control's current value.
               Written the other way round the reopen read as a no-op:
               `setPoComplete(poCompleteChecked)` passed the value already stored,
               which `setPoComplete` correctly resolves to "untouched" and omits from
               the request — so the box moved, the PO was never reopened, and the next
               open showed it complete again. -->
          <div class="rle-po-answer" *ngIf="hasPo && canReopenPo">
            <div class="rle-edit-row">
              <span class="rle-subsection-title">Which delivery completed this PO?</span>
            </div>

            <!-- Writes NOTHING: the PO is already stored complete, so this answer
                 leaves the status row exactly as it is. It is still a decision, and
                 the save carries it as one. -->
            <label class="rle-radio-option">
              <input type="radio" name="poCompletedBy"
                     [checked]="poCompletionAnswer === 'anotherDelivery'"
                     (change)="answerAnotherDeliveryCompletedPo()" />
              <span>A separate logged delivery completed this PO</span>
            </label>

            <!-- The one control on this screen that reopens a whole PO. -->
            <label class="rle-radio-option">
              <input type="radio" name="poCompletedBy"
                     [checked]="poCompletionAnswer === 'noDelivery'"
                     (change)="answerNoDeliveryCompletedPo()" />
              <span>This PO has NOT been completed by any other logged delivery</span>
            </label>

            <div class="rle-empty-note rle-empty-note-warning" *ngIf="reopenDecisionOutstanding">
              Pick one of these before saving.
            </div>
          </div>

          <div class="rle-inline-note" *ngIf="hasPo && !canReopenPo && poCompleteChecked">
            <ion-icon name="information-circle-outline"></ion-icon>
            The PO is marked fully received.
          </div>

          <!-- The consequence of ticking this is NOT here. It lives directly above
               the Save button, with the other consequences of this edit — same
               placement the container editor uses, so both editors put the
               "here is what this does" text in the same place. -->
        </div>

        <!-- Print history — QUANTITY MODE ONLY. Labels are printed per CONTAINER,
             so nothing about editing what arrived on a PO line can invalidate
             one. Showing it in the items editor would invite the user to worry
             about labels an items edit cannot touch. -->
        <div class="rle-card" *ngIf="mode === 'quantity' && details.printHistory as ph">
          <div class="rle-card-header">
            <span class="rle-receipts-title">
              Print history
              <span class="rle-inline-hint" *ngIf="ph.lastPrintedDate">
                — last {{ formatDateTime(ph.lastPrintedDate) }}
              </span>
              <span class="rle-inline-hint" *ngIf="!ph.lastPrintedDate">— never printed</span>
            </span>
            <button type="button" class="rle-reset-btn" *ngIf="ph.prints?.length"
                    (click)="showPrintHistory = !showPrintHistory">
              <ion-icon [name]="showPrintHistory ? 'chevron-up-outline' : 'chevron-down-outline'"></ion-icon>
              {{ showPrintHistory ? 'Hide' : 'Show' }} ({{ ph.printCount }})
            </button>
          </div>

          <!-- OUTSIDE the collapse on purpose. This is the one line that changes a
               decision — labels are already out in the warehouse — and burying it
               behind a toggle would make it the thing most likely to go unread.
               `printWarning === 'none'` says nothing at all, since there is
               nothing out there yet to invalidate. -->
          <div class="rle-inline-note rle-inline-note-warning" *ngIf="printWarning === 'printed'">
            <ion-icon name="warning-outline"></ion-icon>
            Labels have already been printed for this log. Adding or removing
            containers will make those labels wrong, and they will need reprinting
            from the log table.
          </div>

          <ng-container *ngIf="showPrintHistory">
            <div class="rle-print-row" *ngFor="let print of ph.prints">
              <span>{{ formatDateTime(print.printedDate) }}</span>
              <span>{{ print.kind }}</span>
              <span>{{ print.printedBy }}</span>
              <span class="rle-mono">{{ print.itemCount }} item(s)</span>
            </div>
          </ng-container>
        </div>

        <!-- NO "Labels on this log" card here, and none inside the editor below.

             There were two of them, stacked, both captioned for the same thing the
             chips are already captioned for ("Containers on this log") — the title
             was doing the work of saying "this is about containers" three times on
             one screen. The one thing worth showing is what the quantity field will
             READ once this saves, and that is now a single section at the bottom,
             after the edit rather than above it.

             The before/after pair has not been lost: the pre-save confirmation in
             the save bar still shows "Quantity: before → after", which is where
             §6.1 requires both halves — visible before it happens. -->

        <!-- Container editor — QUANTITY MODE ONLY.

             The same chip grid the delivery was logged with on mobile, seeded
             with what the log holds now. Tapping UP mints new containers; tapping
             DOWN tombstones the highest-numbered ones of that type, because each
             of their ids is printed in a QR on a physical box (§3.3). -->
        <div class="rle-card" *ngIf="mode === 'quantity'">
          <!-- Reset sits on the title row rather than under the chips: it is an
               escape hatch, not a step, and it only exists once there is something
               to escape from. -->
          <div class="rle-card-header">
            <span class="rle-receipts-title">Containers on this log</span>
            <button type="button" class="rle-reset-btn" *ngIf="pendingChanges" (click)="resetContainers()">
              <ion-icon name="refresh-outline"></ion-icon>
              Reset
            </button>
          </div>

          <app-container-chips
            [rows]="containerTargetRows"
            [presetTypes]="containerTypes"
            [showPreview]="false"
            (rowsChange)="onContainerRowsChange($event)"
          ></app-container-chips>

          <!-- The two consequence notes this edit raises — renumbering, and
               containers coming off — are NOT here. They live in the save bar,
               directly above the button that commits them. See there. -->

          <ng-container *ngIf="voidCount">
            <!-- Free text, stored verbatim on every container voided by this save
                 and shown against it from then on. Blank stores null — nothing is
                 invented. -->
            <div class="rle-subsection-title">Why are they coming off?</div>
            <div class="rle-edit-row">
              <input class="rle-input" type="text" placeholder="Optional — e.g. only 2 boxes arrived"
                     [(ngModel)]="edit.voidReason" (ngModelChange)="refreshDerived()"
                     aria-label="Why these containers are being voided" />
            </div>
          </ng-container>
        </div>

        <!-- Containers voided EARLIER, on a previous edit. QUANTITY MODE ONLY.

             §1.2 requires these stay visible rather than being filtered out: a
             user who cannot see that a box was voided will wonder why the count
             reads 2 when they logged 3. Collapsed by default because it is
             history, not the task.

             Never captioned "X of N" — a voided row's typeIndex/totalCount are
             frozen at the moment it was voided and a renumber only rewrites
             survivors, so a voided "BOX 2 of 3" can sit next to a surviving
             "BOX 2 of 2". editItemCaption drops the numbering for them. -->
        <div class="rle-card" *ngIf="mode === 'quantity' && voidedItems.length">
          <div class="rle-card-header">
            <span class="rle-receipts-title">Previously voided ({{ voidedItems.length }})</span>
            <button type="button" class="rle-reset-btn" (click)="showVoidedItems = !showVoidedItems">
              <ion-icon [name]="showVoidedItems ? 'chevron-up-outline' : 'chevron-down-outline'"></ion-icon>
              {{ showVoidedItems ? 'Hide' : 'Show' }}
            </button>
          </div>

          <ng-container *ngIf="showVoidedItems">
            <div class="rle-item-row rle-item-voided" *ngFor="let item of voidedItems">
              <div class="rle-item-voided-main">
                <span class="rle-item-caption">{{ itemCaptionFor(item) }}</span>
                <span class="rle-voided-pill">Voided</span>
              </div>
              <span class="rle-voided-attribution">{{ voidAttributionFor(item) }}</span>
            </div>
          </ng-container>
        </div>

        <!-- What the quantity field will read — QUANTITY MODE, AND ONLY ONCE THE
             CHIPS HAVE ACTUALLY MOVED.

             Last section rather than first, because it is the RESULT of the edit
             above it, not context for it. `containersChanged` is the gate: with no
             change there is no new text, and a section that showed the stored value
             under a "new" heading would be claiming an edit that has not happened.

             This is the LOCAL projection. What ends up stored comes back on the save
             response and replaces it (§6.1/§6.2.1). -->
        <!-- BEFORE and AFTER, not just after. A replacement shown on its own asks the
             user to remember what it replaces, and the stored string is exactly the
             thing they came here to correct — "is that right now?" is unanswerable
             without the sentence it is being compared against. -->
        <div class="rle-card" *ngIf="mode === 'quantity' && containersChanged">
          <div class="rle-receipts-title">Quantity field</div>
          <div class="rle-diff-label">Before</div>
          <div class="rle-quantity-preview rle-mono rle-diff-before">{{ quantityPreviewText || '(none)' }}</div>
          <div class="rle-diff-label">After save</div>
          <div class="rle-quantity-preview rle-mono">{{ quantityAfterEdit || '(none)' }}</div>
        </div>

        <!-- The PO line-item check-off — ITEMS MODE ONLY.

             `LineItemCheckoffComponent`, the SAME control the delivery was logged
             with on mobile. A user correcting a delivery should be looking at the
             screen they recorded it on, not at a second dialect of the same idea.

             Two capabilities are turned on that the submit leaves off:
               * `allowClearStatus` — on submit an unmarked line simply writes no
                 receipt, but here clearing an arrival is a real edit that takes its
                 receipt rows OFF, so it has to be something the user can say.
               * `allowCloseToggle` — the submit closes a line through its missing
                 step; here un-ticking the close is the one place this app
                 deliberately sends `completesLine: false`, RE-OPENING a PO line,
                 which is only defensible with the tick in front of the user.
             And `allowOffPoQtyEdit`, because an off-PO row's description is stored
             history that is not ours to rewrite — only its quantity is.

             No `linePanel`: damage is NOT edited here. Sending `damagedQuantity`
             makes the backend re-derive `isDamaged`, so a 0 would erase a report
             whose photos stay attached to the row. The header badge still shows it. -->
        <div class="rle-card rle-receipts" *ngIf="mode === 'items' && details.hasStructuredRows">
          <!-- Parallel to the container editor's header in every part: the same title
               shape ("<things> on this log"), and the same Reset in the same corner,
               appearing only once there is something to escape from. -->
          <div class="rle-card-header">
            <span class="rle-receipts-title">Items on this log</span>
            <button type="button" class="rle-reset-btn" *ngIf="pendingChanges" (click)="resetItemsEdit()">
              <ion-icon name="refresh-outline"></ion-icon>
              Reset
            </button>
          </div>

          <!-- The PO read is a second round trip after the log's own, so this is a
               real wait, not a flicker. Say so rather than showing an empty
               check-off that a user could mistake for "nothing arrived". -->
          <div class="rle-loading" *ngIf="lineItemsSource === 'pending'">
            <ion-spinner name="crescent"></ion-spinner>
            <span>Reading the PO and this log's receipts…</span>
          </div>

          <!-- The degraded path, said out loud. Without po-data there is no ordered
               quantity, so there is no "of remaining" to receive against. -->
          <div class="rle-inline-note rle-inline-note-warning" *ngIf="lineItemsSource === 'receiptsOnly'">
            <ion-icon name="warning-outline"></ion-icon>
            The PO could not be read, so this shows only what the log already
            records. You can still correct what arrived, but there is nothing to
            check it against.
          </div>

          <!-- §1.1: `receipts` is a best-effort sub-fetch, so an EMPTY array means
               either "this log has none" or "the read failed" — and the response is
               a 200 either way. A failed read seeds every PO line with no arrival,
               which renders as the confident claim "nothing arrived on this
               delivery". Saying so is the difference between a degraded screen and a
               lying one. Nothing is silently sent regardless: baseline and edited
               agree, so an untouched line plans no change. -->
          <div class="rle-inline-note rle-inline-note-warning" *ngIf="receiptsState === 'unknown'">
            <ion-icon name="warning-outline"></ion-icon>
            This log's receipts could not be read, so the lines below may show as not
            having arrived when they did. Check against the PO before changing
            anything here.
          </div>

          <div class="rle-empty-note"
               *ngIf="lineItemsSource !== 'pending' && !checkoffRows.length">
            No line items on this log.
          </div>

          <app-items-entry
            [rows]="checkoffRows"
            [damageByLine]="damageByLine"
            [isLegacyPo]="isLegacyPo"
            [expandedIndices]="expandedRowIndices"
            [isLineReadOnly]="isLineReadOnlyForCheckoff"
            [readOnlyNote]="readOnlyNoteForCheckoff"
            [allowClearStatus]="true"
            [allowOffPoQtyEdit]="true"
            [allowOffPoRemove]="true"
            (rowsChange)="onCheckoffRowsChange($event)"
            [allowAddOffPo]="hasPo"
          ></app-items-entry>
        </div>

        <!--
          The shortfall clause.

          This is the ONE part of the note nothing here can rebuild. It came from
          the user's own missing-page selections at submit, and those inputs were
          never persisted; the details payload deliberately does not fetch po-data,
          and the receipt row's `quantityOrdered` is not a substitute (it ignores
          prior deliveries, so a number derived from it would assert a shortfall
          nobody measured).

          So it is a text field, prefilled with what is stored — and when a receipt
          quantity moves, an ANSWER is required before the save goes out. A stale
          number here is the defect; a decision is the fix.

          OFFERED WHETHER OR NOT A CLAUSE IS STORED. It used to be gated on
          `storedMissingClause`, which made the field appear only on a log that already
          recorded a shortfall — so the one case a user cannot fix from anywhere else,
          a delivery that came up short and was submitted without saying so, was also
          the one case with no field to say it in. The gate was never about the risk of
          typing a shortfall; it was about not DERIVING one, and this field derives
          nothing. The text is taken verbatim either way, and an untouched field still
          sends nothing at all (`missingNote` stays null).
        -->
        <div class="rle-card" *ngIf="mode === 'items' && details.hasStructuredRows">
          <div class="rle-receipts-title">Still owed</div>

          <!-- A log that completed the PO had nothing outstanding at the time, by
               definition, so `recomposeItemsNote` drops the clause. Leaving an
               editable field on screen that the save will ignore is worse than
               offering nothing: it invites the user to write something and then
               silently discards it.

               The card stays rather than vanishing — a section that disappears when a
               checkbox is ticked reads as a bug — but it says only why the field is
               gone.

               It deliberately does NOT also announce that a stored clause is being
               deleted. The ribbon at the top of this screen shows the note before and
               after, so the clause visibly leaves the "After save" line the moment
               this is ticked. Repeating it here in prose, quoting the raw clause, is
               the same fact a third time in a vocabulary ("shortfall recorded
               before") the user does not think in. -->
          <div class="rle-inline-note" *ngIf="logCompletesPoChecked">
            <ion-icon name="checkmark-circle-outline"></ion-icon>
            This log completed the PO, so nothing is still owed on it.
          </div>

          <ng-container *ngIf="!logCompletesPoChecked">

          <div class="rle-notice rle-notice-warning" *ngIf="missingReviewNeeded">
            <ion-icon name="help-circle-outline"></ion-icon>
            <div>
              <div class="rle-state-title">Is this still right?</div>
              <div>
                You changed what arrived, so what is still owed may have moved with
                it. Nothing here can work that out — this text is what someone
                recorded at delivery, not a calculation — so please correct it or
                confirm it reads right.
              </div>
              <div class="rle-mono">Was: {{ storedMissingClause }}</div>
            </div>
          </div>

          <!-- The QUESTION comes before the field that answers it.
               It used to sit underneath, so the prompt telling the user the save was
               blocked appeared below the box they were being asked to fill in — and
               its own copy had to say "above" to point back at it. -->
          <div class="rle-notice rle-notice-warning" *ngIf="shortfallNeedsStating">
            <ion-icon name="help-circle-outline"></ion-icon>
            <div>
              <div class="rle-state-title">What is still owed?</div>
              <!-- Two different questions. Reopening a PO retracts a claim that
                   nothing was outstanding, so it is asking what changed; leaving a
                   never-complete PO alone is just asking to have the gap recorded. -->
              <div *ngIf="reopeningPo">
                You are reopening this PO, which says the order is not finished after
                all. Record what is still owed below — the note may still be carrying
                what was written before it was marked complete — or confirm there is
                nothing outstanding.
              </div>
              <!-- Deliberately about THIS DELIVERY, not about the PO. A later
                   delivery finishing the order says nothing about whether this one
                   arrived short, so the question is not "is the PO complete". -->
              <div *ngIf="!reopeningPo">
                This log does not record having completed the PO, so this delivery may
                have arrived short. Say what is still owed below, or confirm nothing
                is.
              </div>
              <div class="rle-save-actions">
                <button type="button" class="rle-choice" (click)="declareNoMissingItems()">
                  Nothing is still owed
                </button>
              </div>
            </div>
          </div>

          <!-- The clause's fixed head is a LABEL, not something to retype.

               Every stored shortfall starts with these words, and they are how
               `splitPreservedNote` recognises the clause on the next edit — a typo
               in them ("MISSING LINE ITMES 1") would stop it being read as a
               shortfall at all and it would ride through as manual text instead.
               So the user owns only the part that is theirs: which lines, and how
               many. Upper-cased as they type, because the note is a stored
               upper-case string and lower-case text spliced into it reads as
               something else's. -->
          <div class="rle-clause-row">
            <span class="rle-clause-head">{{ missingNoteHead }}</span>
            <input class="rle-input rle-clause-input" type="text"
                   autocapitalize="characters"
                   [ngModel]="missingNoteTail" (ngModelChange)="missingNoteTail = $event"
                   placeholder="e.g. 1 (X8), 3"
                   aria-label="Missing items text" />
          </div>
          <!-- The confirm button belongs to the OTHER half of the one gate: a stored
               clause that a quantity edit has made suspect. When there is nothing
               stated to confirm — or a reopen that the stored text cannot answer for
               — the "Nothing is still owed" button above is the way through instead. -->
          <div class="rle-save-actions" *ngIf="shortfallNeedsConfirming">
            <button type="button" class="rle-choice" (click)="confirmMissingNote()">
              This still reads right
            </button>
          </div>
          <!-- Two different sentences, because the field is now offered on a log with
               no stored shortfall too, and "clearing this removes it" is meaningless
               there. Say what the empty field DOES instead: nothing, until typed in. -->
          <div class="rle-inline-note" *ngIf="!shortfallNeedsConfirming && storedMissingClause">
            <ion-icon name="information-circle-outline"></ion-icon>
            Clearing this field removes the shortfall from the note entirely.
          </div>
          <div class="rle-inline-note" *ngIf="!shortfallNeedsConfirming && !storedMissingClause">
            <ion-icon name="information-circle-outline"></ion-icon>
            This log records nothing as still owed. Type here to add it to the items
            note — leave it empty and nothing is added.
          </div>

          </ng-container>
        </div>


        <!-- Save bar -->
        <div class="rle-card rle-save-bar">
          <div class="rle-empty-note rle-empty-note-warning" *ngFor="let problem of validationProblems">
            {{ problem }}
          </div>
          <div class="rle-empty-note rle-empty-note-warning" *ngIf="shortfallNeedsConfirming">
            Confirm or correct what is still owed, above, before saving.
          </div>

          <!-- §3.4: warn BEFORE saving, not after. printWarning weights how much this
               matters — labels that exist in the world are already wrong once this
               saves; the authoritative list of what moved is on the response. -->
          <div class="rle-notice rle-notice-warning" *ngIf="saveState === 'confirming'">
            <ion-icon name="warning-outline"></ion-icon>
            <div>
              <div>
                This changes the item rows, so every label of an affected type is
                renumbered — not just the ones you added or voided.
              </div>
              <div *ngIf="printWarning === 'printed'">
                Labels have already been printed for this log, so the ones in the
                warehouse will be wrong until they are reprinted from the log table.
              </div>
              <!-- Before AND after, per §6.1: on a legacy log gaining containers the
                   derived quantity REPLACES the only free-text record of what
                   arrived, so both halves have to be visible before it happens. -->
              <div class="rle-mono">
                Quantity: {{ quantityPreviewText || '(nothing)' }} → {{ quantityAfterEdit || '(nothing)' }}
              </div>
            </div>
          </div>

          <!-- The consequences of the container edit, DIRECTLY above the button
               that commits them — QUANTITY MODE ONLY.

               Moved out of the chips card and restyled from grey info to amber
               warning, matching the "labels have already been printed" note: both
               are about physical stickers going wrong in the warehouse, which is a
               warning, not a fact of passing interest. Grey info text beside a
               control the user is actively poking is read as chrome; the same words
               under their thumb at the moment of committing are read.

               Both are conditional on the edit itself, so neither can fire on an
               untouched screen. -->
          <div class="rle-inline-note rle-inline-note-warning"
               *ngIf="mode === 'quantity' && addsToExistingContainerType">
            <ion-icon name="warning-outline"></ion-icon>
            <!-- ONLY when the addition lands on a type the log already has. A
                 brand-new type renumbers nothing, and a note that fires either way
                 teaches the user to skip it (§3.4). -->
            Adding to a type already on this log renumbers every label of that
            type, not just the new ones.
          </div>
          <!-- ITEMS-mode consequences, in the same place and the same amber the
               container editor puts its own — so a user moving between the two
               editors finds "what this will do" in one spot rather than two.

               Both of these reach past this delivery: PO completion is ONE ROW PER
               PO, and reopening a line changes what the PO screen says about it for
               everyone. -->
          <div class="rle-inline-note rle-inline-note-warning"
               *ngIf="mode === 'items' && edit.completesPo !== null">
            <ion-icon name="warning-outline"></ion-icon>
            {{ edit.completesPo
                ? 'This marks the whole PO fully received, not just this delivery.'
                : 'This reopens the whole PO, not just this delivery.' }}
          </div>
          <div class="rle-inline-note rle-inline-note-warning"
               *ngIf="mode === 'items' && reopenedLineCount">
            <ion-icon name="warning-outline"></ion-icon>
            {{ reopenedLineCount === 1 ? 'A PO line' : reopenedLineCount + ' PO lines' }}
            will reopen, so the PO will read short on {{ reopenedLineCount === 1 ? 'it' : 'them' }}
            again until the rest arrives.
          </div>

          <!-- What this void DOES, not how it is stored.

               This used to explain tombstoning: that the rows stay on record as
               voided rather than being deleted, because their ids are printed in QRs
               on real boxes. That is true, and it is load-bearing (§3.3 — a scanned
               sticker has to keep resolving), but it is not something the person
               doing the voiding can act on. Their model is "I logged 3, only 2 came,
               I am fixing it to 2"; whether row 3 is flagged or deleted is
               bookkeeping.

               Meanwhile the consequence they CAN act on — the surviving labels of
               that type are renumbered, so stickers in the warehouse are now wrong —
               was not said here at all. It only appeared at the confirm step, which
               is one press too late to change the decision.

               Renumbering is conditional, not automatic: voiding an ENTIRE type
               leaves no survivors to renumber (see `voidRenumbersSurvivors`), and the
               reprint half only matters if labels were ever printed. Claiming either
               unconditionally is how a user learns to click past the one that counts
               (§3.4). -->
          <div class="rle-inline-note rle-inline-note-warning"
               *ngIf="mode === 'quantity' && voidCount">
            <ion-icon name="warning-outline"></ion-icon>
            <span>
              {{ voidCount === 1 ? '1 container' : voidCount + ' containers' }} will come
              off this log.
              <ng-container *ngIf="voidRenumbersSurvivors">
                Every remaining label of that type is renumbered<ng-container
                  *ngIf="printWarning === 'printed'">, so the stickers already on those
                  boxes will be wrong until they are reprinted from the log table</ng-container>.
              </ng-container>
            </span>
          </div>

          <div class="rle-save-actions">
            <button type="button" class="rle-retry-btn"
                    [disabled]="!canSave"
                    (click)="requestSave()">
              <ion-spinner name="crescent" *ngIf="saveState === 'saving'"></ion-spinner>
              {{ saveState === 'confirming' ? 'Save anyway' : 'Save changes' }}
            </button>
            <button type="button" class="rle-choice" *ngIf="saveState === 'confirming'" (click)="cancelConfirm()">
              Cancel
            </button>
          </div>

          <!-- A greyed button with no stated reason is indistinguishable from a
               broken one. Three of the five conditions behind `canSave` get a
               sentence here, each saying what to do next rather than what is wrong; a
               save in flight speaks through the spinner and a landed save through the
               outcome block below, so both are deliberately silent here. -->
          <div class="rle-muted rle-save-blocked" *ngIf="whySaveDisabled as reason">
            {{ reason }}
          </div>

          <!-- THE OUTCOME, directly under the button that caused it.

               It used to be a separate card further down the page, which meant a
               save's result appeared somewhere other than where the save happened —
               and repeated the confirmation the disabled-button text was already
               giving ("Saved. Close and reopen…" immediately above "✓ Saved."). One
               place, one confirmation, one statement of what changed.

               A 200 on this endpoint ALWAYS means the transaction committed:
               `outcome` is never "Failed" (a refusal is a 400 and a failure a 500),
               so nothing here presents a save as not having happened. -->
          <ng-container *ngIf="saveState === 'saved' && savedView">
            <div class="rle-inline-note rle-saved-note" *ngIf="savedView.savedCleanly">
              <ion-icon name="checkmark-circle-outline"></ion-icon>
              <!-- §4.4: a PUT that changes nothing is a legitimate 200, not an error. -->
              {{ savedView.nothingChanged ? 'Saved — nothing needed changing.' : 'Saved.' }}
            </div>
            <div class="rle-inline-note rle-inline-note-warning" *ngIf="!savedView.savedCleanly">
              <ion-icon name="warning-outline"></ion-icon>
              Saved. The row changes committed, but something after that didn't.
            </div>

            <!-- `incompleteParts` is the list of what ACTUALLY went wrong. NOT derived
                 from the `*Status` flags, whose `isSuccess: false` placeholders mean
                 "not attempted" (§6). -->
            <div class="rle-empty-note rle-empty-note-warning" *ngFor="let warning of savedView.warnings">
              {{ warning }}
            </div>

            <!-- §6.1: the before AND after. On a legacy log gaining containers the
                 derived quantity OVERWRITES the only free-text record of what
                 arrived, so both halves have to be stated. -->
            <div class="rle-inline-note" *ngIf="mode === 'quantity' && quantityNotice">
              <ion-icon name="information-circle-outline"></ion-icon>
              {{ quantityNotice }}
            </div>

            <!-- What an ITEMS save stored, in the note's own words — read back from
                 the response rather than from the local projection. A titled block,
                 not a label/value row: the note is a SENTENCE, and right-aligning it
                 opposite a label turns it into a fragment. -->
            <ng-container *ngIf="mode === 'items'">
              <div class="rle-diff-label">Items field now reads</div>
              <div class="rle-note-text">{{ savedView.storedNote || '(empty)' }}</div>
            </ng-container>

          </ng-container>
        </div>

        <!--
          ===================================================================
          OUTCOME (§4, §6, §7)
          ===================================================================
          Three blocks, in the order the user needs them: the conflict (which has
          already changed the screen under them), the refusals, and the save
          result.

          Two things here are contract, not layout:
            * the renumber warning is a BLOCK, never a toast — a non-empty
              `renumberedItems` means stickers already on boxes in the warehouse
              are wrong (§6.2.3). This screen offers no print button; it names
              the wrong labels and says where to reprint them;
            * the conflict block has NO retry button. The `PUT` is not idempotent,
              so replaying it doubles minted item positions and inserted receipt
              rows (§5.2). The only way forward is to redo the edit.
        -->

        <!-- 409 — a concurrent edit stopped this one. The screen has ALREADY been
             re-rendered from the conflict's `detail`, so this block's job is to say
             so; without it the user is silently looking at different data than the
             data they edited. Expect this routinely, not rarely (§5.3). -->
        <div class="rle-card rle-notice rle-notice-warning rle-notice-top" *ngIf="conflictMessage">
          <ion-icon name="git-compare-outline"></ion-icon>
          <div>
            <div class="rle-state-title">Your edit was not applied</div>
            <!-- Verbatim: the API's message names which ids appeared or disappeared. -->
            <div>{{ conflictMessage }}</div>
            <div class="rle-muted">
              The log above has been reloaded with what is on it now. Redo the change
              against this version — it cannot be re-sent as it was.
            </div>
            <div class="rle-save-actions">
              <button type="button" class="rle-choice" (click)="reloadAfterConflict()">Reload log</button>
              <button type="button" class="rle-choice" (click)="acknowledgeConflict()">Dismiss</button>
            </div>
          </div>
        </div>

        <!-- 400 / 500 / network — nothing was written and the log is exactly as it
             was. The message is the API's own body, shown as-is: a 400 names the
             offending ids and says what to do instead (§4.1). Note there is no
             "set the PO first" affordance anywhere: a log's PO cannot be changed
             after submit by any endpoint (§3.5/§4.3). A 404 is not here — it takes
             the screen to its not-found state, because there is nothing left to
             edit. -->
        <div class="rle-card rle-notice rle-notice-warning rle-notice-top" *ngIf="saveState === 'refused' && refusal">
          <ion-icon name="alert-circle-outline"></ion-icon>
          <div>
            <div class="rle-state-title">
              {{ refusal.kind === 'serverError' ? 'The save failed' : 'The edit was refused' }}
            </div>
            <div>{{ refusal.message }}</div>
            <div class="rle-muted" *ngIf="refusal.nothingWasWritten">
              Nothing was saved — the log is exactly as it was, and your changes are
              still on this screen.
            </div>
          </div>
        </div>

        <!-- The 200's remaining block. The confirmation, the warnings, the quantity
             before/after and the stored note all moved UP into the save bar, so that
             a save's result appears where the save happened rather than in a second
             card further down. What stays here is the one thing that is not about
             this screen at all. -->
        <ng-container *ngIf="saveState === 'saved' && savedView">

          <!-- §6.2.3 — SAID EXPLICITLY, as a block. Not a toast, and not a print
               button: reprinting is a manual step through the log table's reprint
               action, taken at the printer with blank stock. Removing the button
               does not remove the obligation to name the labels that went wrong,
               and this response is the only place those facts exist. -->
          <div class="rle-card rle-notice rle-notice-warning rle-notice-top" *ngIf="renumberWarning">
            <ion-icon name="warning-outline"></ion-icon>
            <div>
              <div class="rle-state-title">Labels need replacing</div>
              <div>{{ renumberWarning }}</div>
              <div class="rle-mono" *ngFor="let caption of savedView.renumberedCaptions">
                {{ caption }}
              </div>
              <div class="rle-muted">
                Close this and use the log table's reprint action on this entry when
                you are ready to print.
              </div>
            </div>
          </div>

        </ng-container>

        <!-- NO photo panels here. The entry-details modal this one is nested in
             already shows the log's photos and owns adding/removing them, so a
             second copy would be two places to look at the same thing and two
             sets of hour-limited SAS URLs to keep fresh. -->

      </ng-container>

    </ion-content>
  </ng-template>
</ion-modal>
